import { useRef, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { FileNode } from '@/lib/fileUtils';
import { useTranslation } from 'react-i18next';

export interface EditFolderModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  folderName: string;
  onFolderNameChange: (value: string) => void;
  validation: { valid: boolean; error: string | null };
  editingFolder: FileNode | null;
  onEdit: () => void;
  onClose: () => void;
  isEditing: boolean;
}

export function EditFolderModal({
  isOpen,
  onOpenChange,
  folderName,
  onFolderNameChange,
  validation,
  editingFolder,
  onEdit,
  onClose,
  isEditing,
}: EditFolderModalProps) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const isUnchanged = editingFolder && folderName.trim() === editingFolder.name;

  // 打开弹窗时聚焦并智能定位光标
  useEffect(() => {
    if (isOpen && editingFolder) {
      setTimeout(() => {
        const input = inputRef.current;
        if (input) {
          input.focus();
          const name = input.value;
          if (editingFolder.type === 'file') {
            const lastDotIndex = name.lastIndexOf('.');
            if (lastDotIndex > 0) {
              // 文件：光标定位到最后一个点前面，不选中任何文本
              input.setSelectionRange(lastDotIndex, lastDotIndex);
            } else {
              input.setSelectionRange(name.length, name.length);
            }
          } else {
            // 文件夹：光标放在末尾
            input.setSelectionRange(name.length, name.length);
          }
        }
      }, 50);
    }
  }, [isOpen, editingFolder]);

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold">
            {editingFolder?.type === 'file'
              ? t('project.editFile')
              : t('project.editFolder')}
          </DialogTitle>
        </DialogHeader>
        <div className="px-6 py-4">
          <div className="relative">
            <input
              type="text"
              value={folderName}
              onChange={e => onFolderNameChange(e.target.value.slice(0, 32))}
              placeholder={t('project.folderName')}
              className={`w-full p-3 text-sm border rounded-lg focus:outline-none focus:ring-1 bg-white ${
                validation.error
                  ? 'border-red-400 focus:ring-red-300'
                  : 'border-gray-400 focus:ring-blue-300'
              }`}
              ref={inputRef}
              onKeyDown={e => {
                if (
                  e.key === 'Enter' &&
                  !isEditing &&
                  validation.valid &&
                  editingFolder &&
                  folderName.trim() !== editingFolder.name
                ) {
                  onEdit();
                }
              }}
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">
              {folderName.length}/32
            </span>
          </div>
          {validation.error && (
            <p className="mt-1 text-xs text-red-500">{validation.error}</p>
          )}
        </div>
        <DialogFooter className="flex justify-end gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={isEditing}
            className="px-4 py-2"
          >
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            onClick={onEdit}
            disabled={!validation.valid || isEditing || !!isUnchanged}
            className="px-4 py-2 bg-gray-800 text-white hover:bg-gray-700 disabled:opacity-50"
          >
            {isEditing ? (
              <span className="flex items-center gap-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b border-white"></div>
                {t('common.saving')}
              </span>
            ) : (
              t('common.save')
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default EditFolderModal;
